import React, { useState } from 'react';
import { Button, Modal, Spinner } from 'flowbite-react';
import { checkConnectivity } from '../../../utils/useNetworkQuality';

export default function GuardedActionButton({
  onAction,
  disabled,
  label = 'Proceed',
  className = '',
  loading = false,
  spinnerOnChecking = true,
}) {
  const [checking, setChecking] = useState(false);
  const [showOffline, setShowOffline] = useState(false);
  const [showPoor, setShowPoor] = useState(false);

  async function handleClick() {
    if (disabled || checking || loading) return;
    // Immediate offline short-circuit for faster UX
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setShowOffline(true);
      return;
    }
    setChecking(true);
    try {
      const { online, status } = await checkConnectivity({ timeoutMs: 1500 });
      if (!online) {
        setShowOffline(true);
        return;
      }
      if (status === 'poor') {
        setShowPoor(true);
        return;
      }
      await onAction?.();
    } finally {
      setChecking(false);
    }
  }

  async function recheckAndMaybeProceed() {
    setChecking(true);
    try {
      const { online, status } = await checkConnectivity({ timeoutMs: 1500 });
      if (!online || status === 'poor') return; // keep modal open
      setShowOffline(false);
      setShowPoor(false);
      await onAction?.();
    } finally {
      setChecking(false);
    }
  }

  return (
    <>
      <Button disabled={disabled || checking || loading} className={className} onClick={handleClick}>
        {(loading || (checking && spinnerOnChecking)) ? (
          <>
            <Spinner aria-label="Medium sized spinner example" size="sm" className="mr-2" /> {label}
          </>
        ) : (
          label
        )}
      </Button>

      <Modal show={showOffline} size="md" onClose={() => setShowOffline(false)} popup>
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <div className="mb-2 text-lg font-semibold text-red-600">You're offline</div>
            <p className="mb-4 text-sm text-gray-600">Please connect to the internet to continue.</p>
            <div className="flex justify-center gap-2">
              <Button color="gray" onClick={() => setShowOffline(false)}>Close</Button>
              <Button onClick={recheckAndMaybeProceed}>
                {checking ? 'Checking…' : 'Try again'}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
      <Modal show={showPoor} size="md" onClose={() => setShowPoor(false)} popup>
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <div className="mb-2 text-lg font-semibold text-yellow-600">Network is unstable/slow</div>
            <p className="mb-4 text-sm text-gray-600">You may experience delays. Do you want to proceed anyway?</p>
            <div className="flex justify-center gap-2">
              <Button color="gray" onClick={() => setShowPoor(false)}>Cancel</Button>
              <Button color="warning" onClick={async () => { setShowPoor(false); await onAction?.(); }}>Proceed anyway</Button>
              <Button onClick={recheckAndMaybeProceed}>
                {checking ? 'Checking…' : 'Check again'}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}
